/**
 * ModalDA.jsx — Primitives modals DA.
 *
 * §11/§18 — Toute modal compose ces primitives ; jamais de clone local.
 * §20 — Tokens importés de ./theme (source unique). Constantes locales supprimées.
 *
 * Rétrocompatibilité : les constantes ACCENT, ACCENT_08, INK, MUTED sont
 * ré-exportées pour ne pas casser les consommateurs existants.
 * MUTED aligné sur COLORS.muted (0.55) — §20 vague 3, validé 2026-06-12.
 * Typographie : Roboto = font principale de l'app (chargée via src/index.css).
 * Inter était déclarée dans theme.js mais non chargée — corrigé 2026-06-06.
 */
import React, { useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import {COLORS, FONTS, RADII, media, FONT_SIZE, FONT_WEIGHT} from './theme';

/* ─── Ré-exports rétrocompatibles ───────────────────────────────────────────── */
/* preservation-allow §69/§5b : shims morts ACCENT_HOVER/ACCENT_14/ACCENT_45 retirés
   (0 consommateur repo-wide ; tokens dispo via COLORS.accentHover/accent14/accent45). */
export const ACCENT       = COLORS.accent;        /* violet accent DA */
export const ACCENT_08    = COLORS.accentSoft;    /* rgba(...0.08) */
export const INK          = COLORS.ink;           /* '#0d0a10' */
export const MUTED        = COLORS.muted;             /* rgba(13, 10, 16, 0.55) — §20 aligné */

/* ─── Overlay ────────────────────────────────────────────────────────────────── */
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  font-family: ${FONTS.body}; /* §20 anti-drift — FONTS.body depuis theme.js */
  padding: 1rem;
`;

/* ─── Card ───────────────────────────────────────────────────────────────────── */
export const ModalCard = styled.div.attrs({ 'data-ev': 'modal-card' })`
  width: ${(p) => p.$width || '30rem'};
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  background: var(--ev-surface, ${COLORS.white});
  border: 0.0625rem solid var(--ev-line, ${COLORS.line});
  box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.25);
  border-radius: ${RADII.card};

  ${media.sm`max-height: min(80vh, 36rem);`}
`;

/* ─── Header ─────────────────────────────────────────────────────────────────── */
export const ModalHeader = styled.div`
  position: relative;
  background: ${COLORS.accent};
  color: #fff;
  padding: 1.125rem 3.5rem;
  text-align: center;
`;

export const ModalHeaderTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.2;
  color: #fff;
`;

/* ─── Bouton fermeture (croix cerclée §12 sexies) ────────────────────────────── */
export const ModalCloseBtn = styled.button`
  position: absolute;
  top: 50%;
  right: 0.875rem;
  transform: translateY(-50%);
  width: 2rem;
  height: 2rem;
  border-radius: ${RADII.circle};
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  border: 0.0625rem solid rgba(255, 255, 255, 0.4);
  cursor: pointer;
  font-family: inherit;
  font-size: ${FONT_SIZE.base};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.12s;
  &:hover { background: rgba(255, 255, 255, 0.3); }
`;

/**
 * ModalIconBadge — Pastille ronde pour icône contextuelle dans modal body.
 * Usage :
 *   <ModalIconBadge $bg={COLORS.accentSoft} $border $size="5rem">
 *     <svg>…</svg>
 *   </ModalIconBadge>
 *
 * Remplace WalletIconWrap (ConnectWallet) et ClockIconWrap (SessionTimeout).
 * Props :
 *   $size   — taille (default '3rem'). Variantes courantes : 3rem (header), 5rem (body)
 *   $bg     — couleur de fond (default = blanc semi-transparent)
 *   $border — true pour ajouter un border accent (icône violette en body)
 *   $mb     — margin-bottom (default '0.5rem')
 */
export const ModalIconBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$size || '3rem'};
  height: ${(p) => p.$size || '3rem'};
  border-radius: ${RADII.circle};
  background: ${(p) => p.$bg || 'rgba(255, 255, 255, 0.2)'};
  ${(p) => p.$border && `border: 0.125rem solid ${COLORS.accent};`}
  font-size: ${FONT_SIZE.xl};
  color: #fff;
  margin-bottom: ${(p) => p.$mb || '0.5rem'};

  svg { width: 50%; height: 50%; }
`;

/* ─── Body ───────────────────────────────────────────────────────────────────── */
export const ModalBody = styled.div`
  padding: clamp(1rem, 5vw, 1.75rem) clamp(1rem, 5vw, 2.5rem) 1rem;
  text-align: center;

  p {
    margin: 0 0 0.75rem;
    font-size: ${FONT_SIZE.base};
    line-height: 1.5;
    color: var(--ev-ink, ${INK});
  }
  p.small {
    font-size: ${FONT_SIZE.sm};
    color: var(--ev-muted, ${MUTED});
  }
  .bold { font-weight: ${FONT_WEIGHT.bold}; }

  ${media.sm`
    padding: 1rem 1rem 0.75rem;
  `}
`;

/* ─── Footer ─────────────────────────────────────────────────────────────────── */
export const ModalFooter = styled.div`
  padding: 1rem clamp(1rem, 5vw, 2.5rem) 1.75rem;
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  ${media.sm`
    padding: 0.75rem 1rem 1.25rem;
  `}
`;

/* ─── Boutons ────────────────────────────────────────────────────────────────── */
export const ButtonPrimary = styled.button`
  min-width: 11rem;
  padding: 0.85rem 1.5rem;
  background: ${COLORS.accent};
  color: #fff;
  border: none;
  border-radius: ${RADII.button};
  font-family: inherit;
  font-weight: ${FONT_WEIGHT.heavy};
  font-size: ${FONT_SIZE.sm};
  cursor: pointer;
  box-shadow: 0 0.5rem 1.5rem rgba(194, 24, 166, 0.32);
  transition: background 0.15s;
  &:hover:not(:disabled) { background: ${COLORS.accentHover}; }
  &:disabled {
    background: ${COLORS.disabledBg};
    color: ${COLORS.disabledInk};
    box-shadow: none;
    cursor: not-allowed;
  }
`;

export const ButtonSecondary = styled.button`
  min-width: 9rem;
  padding: 0.85rem 1.5rem;
  background: transparent;
  color: var(--ev-ink, ${INK});
  border: 0.0625rem solid var(--ev-ink-line, rgba(13, 10, 16, 0.4));
  border-radius: ${RADII.button};
  font-family: inherit;
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  &:hover {
    background: var(--ev-subtle, rgba(13, 10, 16, 0.04));
    border-color: var(--ev-ink, ${INK});
  }
`;

/* ─── Form primitives ────────────────────────────────────────────────────────── */
export const FormRow = styled.div`
  margin-top: 1rem;
  text-align: left;
`;

export const FormLabel = styled.label`
  display: block;
  font-weight: ${FONT_WEIGHT.bold};
  font-size: ${FONT_SIZE.sm};
  color: var(--ev-ink, ${INK});
  margin-bottom: 0.375rem;
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.75rem;
  background: var(--ev-surface, #fff);
  color: var(--ev-ink, ${INK});
  border: 0.0625rem solid var(--ev-line-strong, ${COLORS.lineStrong});
  border-radius: 0.5rem;
  font-family: inherit;
  font-size: ${FONT_SIZE.base};
  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 0.1875rem ${COLORS.accent14};
  }
  &::placeholder { color: var(--ev-muted, ${MUTED}); }
`;

export const FormSummary = styled.div`
  background: var(--ev-bg-subtle, ${COLORS.bgSubtle});
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  margin: 1rem 0;
  font-size: 0.9375rem;
  text-align: left;
  color: var(--ev-ink, ${INK});
  .label {
    color: var(--ev-muted, ${MUTED});
    font-size: ${FONT_SIZE.xs};
  }
  .bold { font-weight: ${FONT_WEIGHT.bold}; }
`;

/* ─── Spinner ────────────────────────────────────────────────────────────────── */
const spin = keyframes`to { transform: rotate(360deg); }`;

export const Spinner = styled.div`
  display: inline-block;
  width: 1.5rem;
  height: 1.5rem;
  border: 0.1875rem solid ${COLORS.accentSoft};
  border-top-color: ${COLORS.accent};
  border-radius: ${RADII.circle};
  margin-bottom: 0.5rem;
  animation: ${spin} 1s linear infinite;
`;

/* ─── Variants Header — form/edit modals (text-align: left, sober) ──────────────
 * §19 enrichissement opportuniste — pattern dupliqué dans EditAccountModal (P1) +
 * createScene + importScene (P3 batch 4 — leçons capitalisées). Extraction dans la
 * primitive plutôt que dans 3 fichiers voisins.
 *
 * Usage :
 *   <ModalHeaderLeft>
 *     <ModalHeaderTitleLeft>Account settings</ModalHeaderTitleLeft>
 *     <ModalCloseBtn onClick={onClose} aria-label="Close">✕</ModalCloseBtn>
 *   </ModalHeaderLeft>
 */
export const ModalHeaderLeft = styled(ModalHeader)`
  text-align: left;
  padding: 1.1rem 1.5rem;
`;

export const ModalHeaderTitleLeft = styled(ModalHeaderTitle)`
  font-weight: ${FONT_WEIGHT.extrabold};
  letter-spacing: normal;
  text-transform: none;
`;

/* NOTE : ModalSubtitle retiré (axe 7 DA audit, 2026-06-07) — 0 consommateurs.
 * Clones locaux dans createScene.tsx + importScene.tsx → candidat propagation future (axe 8). */
